/**
 * HeidiMail Analytics Tracker
 *
 * Tracks clicks, navigation, and scroll depth.
 * Batches events and flushes every 3s or on page hide/unload.
 * In production: encrypts event payload with AES-GCM using correlation ID as key.
 * In development: sends plaintext JSON.
 *
 * Also records the page view's heatmap (heatmap-recorder.ts). Opened with ?swp-heatmap=1 (the dashboard's heatmap
 * view) the page is being looked at, not used: nothing is tracked, and heatmap-overlay.js is loaded to draw the heatmap.
 */

import { heatmapModeParameter } from './heatmap-protocol';
import { startHeatmapRecording } from './heatmap-recorder';

// === Config ===

const IS_DEV = location.hostname === 'localhost' || location.hostname === '127.0.0.1';
let correlationId: string | null = null;

// === Capture correlation ID from any response ===

const _origFetch = window.fetch;
window.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
    const response = await _origFetch.call(this, input, init);
    const cid = response.headers.get('X-Correlation-Id');
    if (cid) correlationId = cid;
    return response;
};

// Capture from initial page load (document response doesn't go through fetch)
// We'll get it from the first fetch response or from a meta tag if available
const metaCid = document.querySelector('meta[name="x-correlation-id"]')?.getAttribute('content');
if (metaCid) correlationId = metaCid;

// === Encryption (AES-256-GCM) ===

async function deriveKey(secret: string): Promise<CryptoKey> {
    const encoded = new TextEncoder().encode(secret);
    const hash = await crypto.subtle.digest('SHA-256', encoded);
    return crypto.subtle.importKey('raw', hash, 'AES-GCM', false, ['encrypt']);
}

async function encrypt(plaintext: string, secret: string): Promise<{ iv: string; data: string }> {
    const key = await deriveKey(secret);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encoded = new TextEncoder().encode(plaintext);
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
    return {
        iv: btoa(String.fromCharCode(...iv)),
        data: btoa(String.fromCharCode(...new Uint8Array(ciphertext))),
    };
}

// === Event buffer ===

interface TrackerEvent {
    path: string;
    eventType: string;
    element?: string;
    action?: string;
    label?: string;
    value?: string;
}

const buffer: TrackerEvent[] = [];
let flushTimer: number | null = null;

function trackEvent(evt: Partial<TrackerEvent> & { eventType: string }) {
    buffer.push({
        path: evt.path ?? location.pathname,
        eventType: evt.eventType,
        element: evt.element,
        action: evt.action,
        label: evt.label,
        value: evt.value,
    });
    scheduleFlush();
}

function scheduleFlush() {
    if (flushTimer !== null) return;
    flushTimer = window.setTimeout(() => {
        flushTimer = null;
        flush();
    }, 3000);
}

async function flush() {
    if (buffer.length === 0) return;

    const events = buffer.splice(0, buffer.length);

    for (const evt of events) {
        const json = JSON.stringify(evt);
        const headers: Record<string, string> = {};

        let body: string;

        if (!IS_DEV && correlationId) {
            // Production: encrypt with correlation ID
            const encrypted = await encrypt(json, correlationId);
            body = JSON.stringify(encrypted);
            headers['Content-Type'] = 'application/json';
            headers['X-Correlation-Id'] = correlationId;
            headers['X-Encrypted'] = '1';
        } else {
            // Development: plaintext
            body = json;
            headers['Content-Type'] = 'application/json';
            if (correlationId) headers['X-Correlation-Id'] = correlationId;
        }

        if (document.visibilityState === 'hidden') {
            navigator.sendBeacon('/api/analytics/event',
                new Blob([body], { type: 'application/json' }));
        } else {
            _origFetch('/api/analytics/event', {
                method: 'POST',
                headers,
                body,
                keepalive: true,
            }).catch(() => { /* fire-and-forget */ });
        }
    }
}

// === Click tracking ===

function trackClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    const trackable = target.closest('a, button, [data-track]');
    if (!trackable) return;

    trackEvent({
        eventType: 'click',
        element: cssSelector(trackable as HTMLElement),
        action: 'click',
        label: getLabel(trackable as HTMLElement),
        value: (trackable as HTMLAnchorElement).href || undefined,
    });
}

// === Navigation tracking ===

let lastPath = location.pathname;

function trackNavigation() {
    const currentPath = location.pathname;
    if (currentPath !== lastPath) {
        trackEvent({
            eventType: 'navigation',
            path: currentPath,
            action: 'navigate',
            label: document.title,
            value: lastPath,
        });
        lastPath = currentPath;
        scrollMilestones.clear();
    }
}


// === Scroll depth tracking ===

const scrollMilestones = new Set<number>();
const MILESTONES = [25, 50, 75, 100];
let scrollThrottled = false;

function trackScroll() {
    if (scrollThrottled) return;
    scrollThrottled = true;

    requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) { scrollThrottled = false; return; }

        const pct = Math.round((scrollTop / docHeight) * 100);

        for (const milestone of MILESTONES) {
            if (pct >= milestone && !scrollMilestones.has(milestone)) {
                scrollMilestones.add(milestone);
                trackEvent({
                    eventType: 'scroll',
                    action: `scroll_${milestone}`,
                    label: `${milestone}%`,
                    value: String(milestone),
                });
            }
        }

        scrollThrottled = false;
    });
}

// === Start ===

function startEventTracking() {
    document.addEventListener('click', trackClick, true);
    window.addEventListener('popstate', trackNavigation);
    window.addEventListener('scroll', trackScroll, { passive: true });

    trackEvent({
        eventType: 'navigation',
        action: 'pageview',
        label: document.title,
    });

    // Flush on page hide/unload
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
    });
    window.addEventListener('beforeunload', () => flush());
}

/**
 * Loads heatmap-overlay.js next to this script. It only accepts the heatmap from the dashboard's origin, given on the
 * tracker's script tag as data-swp-heatmap-origin; without it there is no heatmap.
 */
function loadHeatmapOverlay(tracker: HTMLScriptElement | null) {
    const adminOrigin = tracker?.dataset.swpHeatmapOrigin;
    if (!tracker || !adminOrigin) return;

    const overlay = document.createElement('script');
    overlay.src = new URL('heatmap-overlay.js', tracker.src).href;
    overlay.dataset.adminOrigin = adminOrigin;
    document.head.appendChild(overlay);
}

// document.currentScript is only set while this script runs, so it is read here, not later.
const trackerScript = document.currentScript as HTMLScriptElement | null;

if (new URLSearchParams(location.search).has(heatmapModeParameter)) {
    loadHeatmapOverlay(trackerScript);
} else {
    startEventTracking();
    startHeatmapRecording();
}

// === Helpers ===

function cssSelector(el: HTMLElement): string {
    const tag = el.tagName.toLowerCase();
    const id = el.id ? `#${el.id}` : '';
    const classes = el.className && typeof el.className === 'string'
        ? '.' + el.className.trim().split(/\s+/).slice(0, 2).join('.')
        : '';
    const parent = el.parentElement;
    const parentTag = parent ? parent.tagName.toLowerCase() : '';
    return `${parentTag ? parentTag + ' > ' : ''}${tag}${id}${classes}`;
}

function getLabel(el: HTMLElement): string {
    return el.getAttribute('aria-label')
        ?? el.getAttribute('data-track')
        ?? el.textContent?.trim().slice(0, 80)
        ?? '';
}
